import {
  ChecklistFieldDef,
  ChecklistSectionDef,
  normalizeDeedIdForSelect,
  repeatableEntriesKey,
  sectionFlowMode,
} from "../components/DynamicChecklistManagement/DynamicChecklistManagement";
import { checklistValueForMappedSave } from "./checklistDateFormat";
import { isChecklistTruthyScalar } from "./dynamicChecklistDropdownValue";
import {
  parseSurveyNatureFieldScopeKey,
  resolveVisibleSurveyNatureGroups,
  surveyLinkedGroups,
  surveyNatureFieldScopeKey,
} from "./dynamicChecklistSurveyNature";
import {
  SURVEY_HIERARCHY_META,
  SurveyHierarchyLevel,
  buildMeasurementDirectionSaveEntry,
  flattenSurveyHierarchyRows,
  GeneralDetailsDocumentHierarchy,
  isMeasurementDirectionLabel,
  isMeasurementDirectionTag,
  isSurveyFlatWriteTag,
  isSurveyHierarchyWriteTag,
  isSurveyNatureScopedScheduleTag,
  isStandardMeasurementDirectionKey,
  MEASUREMENT_DIRECTION_TAG_PREFIX,
  MEAS_DIR_ID_BY_LABEL_PREFIX,
  normalizeDirectionLabel,
  parseCustomMeasurementLists,
  patchHierarchyRowMeta,
  enrichHierarchyRowMeta,
  buildHierarchyRowMetaIds,
  listSurveyDocumentFdIdsWithSchedules,
  resolveSurveyDocumentFdWithSchedules,
  resolveSurveyMappedFieldTag,
  resolveSurveyTypeFromHierarchyRow,
  resolveSurveyHierarchySaveTag,
  isSurveyFieldVisibleForSurveyType,
  normalizeBoundaryDirectionForGdStorage,
  resolveBoundaryAsPerFromTaggedFields,
  isBoundaryAsPerTag,
  resolveScheduleAsPerFromTaggedFields,
  isScheduleAsPerTag,
  surveyHierarchyGroupForLevel,
  surveyHierarchyLinkedGroups,
} from "./dynamicChecklistSurveyHierarchy";

export type SurveyHierarchyRowMeta = {
  scheduleId?: number;
  surId?: number;
  boundaryId?: number;
  measurementId?: number;
};

export type SurveyHierarchyRowUpdate = {
  level: SurveyHierarchyLevel;
  rowId: number;
  meta?: SurveyHierarchyRowMeta;
  fields: Record<string, string>;
  measurementDirections?: SurveyMeasurementDirectionUpdate[];
};

export type SurveyMeasurementDirectionUpdate = {
  labelKey: string;
  value: string;
  measDirId?: number;
  measureCustom?: string;
};

export type SurveyFlatVerificationUpdate = {
  fdId: number;
  fields: Record<string, string>;
};

export type SurveyNatureScopedUpdate = {
  fdId: number;
  scheduleId: number;
  fields: Record<string, string>;
};

/** Survey flat tags → DB target (see SURVEY_FLAT_TAG_TARGET in dynamicChecklistSurveyHierarchy.ts). */
const HIERARCHY_LEVEL_META_ID: Record<SurveyHierarchyLevel, string> = {
  schedule: SURVEY_HIERARCHY_META.SCHEDULE_ID,
  survey: SURVEY_HIERARCHY_META.SUR_ID,
  boundary: SURVEY_HIERARCHY_META.BOUNDARY_ID,
  measurement: SURVEY_HIERARCHY_META.MEASUREMENT_ID,
};

function fieldTag(field: ChecklistFieldDef): string {
  return resolveSurveyMappedFieldTag(field);
}

function isMeasurementOnlyHierarchyTag(tag: string): boolean {
  const t = tag.trim();
  if (!t) return false;
  if (isMeasurementDirectionTag(t)) return true;
  return t === "gdMeasDir_measure_custom" || t === "gdMeasDir_label_name" || t === "gdMeasDir_value";
}

function collectHierarchyTaggedFields(
  fields: ChecklistFieldDef[],
  row: Record<string, string>
): Record<string, string> {
  const surveyType = resolveSurveyTypeFromHierarchyRow(fields, row);
  const out: Record<string, string> = {};
  let hasBoundaryAsPerMappedField = false;
  let hasScheduleAsPerMappedField = false;
  fields.forEach((f) => {
    if (f.disabled) return;
    if (!isSurveyFieldVisibleForSurveyType(f, surveyType, row)) return;
    const tag = resolveSurveyHierarchySaveTag(f, row, fields);
    if (!tag || !isSurveyHierarchyWriteTag(tag) || isMeasurementOnlyHierarchyTag(tag)) return;
    let val = checklistValueForMappedSave(f, row[f.id]);
    if (isBoundaryAsPerTag(tag)) {
      hasBoundaryAsPerMappedField = true;
      out[tag] = isChecklistTruthyScalar(val) ? "true" : "false";
      return;
    }
    if (isScheduleAsPerTag(tag)) {
      hasScheduleAsPerMappedField = true;
      out[tag] = isChecklistTruthyScalar(val) ? "true" : "false";
      return;
    }
    if (tag === "gdSchedule_scheduleasper" && f.type === "checkbox") {
      hasScheduleAsPerMappedField = true;
      out.gdSchedule_asPerPatta = isChecklistTruthyScalar(val) ? "true" : "false";
      return;
    }
    if (tag === "gdBoundary_direction") {
      val = normalizeBoundaryDirectionForGdStorage(val);
    }
    if (!val.trim()) return;
    out[tag] = val;
    // Compatibility: many source rows store "Boundaries/Total Extent" in `land_aries`,
    // while some dynamic fields are configured with `gdBoundary_measurement` (or vice versa).
    // Write both so back-update always reaches the expected source column.
    if (tag === "gdBoundary_measurement" && !out.gdBoundary_land_aries) {
      out.gdBoundary_land_aries = val;
    } else if (tag === "gdBoundary_land_aries" && !out.gdBoundary_measurement) {
      out.gdBoundary_measurement = val;
    }
  });
  const boundaryAsPer = resolveBoundaryAsPerFromTaggedFields(out);
  Object.keys(out).forEach((tag) => {
    if (isBoundaryAsPerTag(tag)) delete out[tag];
  });
  if (hasBoundaryAsPerMappedField) {
    out.gdBoundary_boundary_as_per = boundaryAsPer;
  }
  const scheduleAsPer = resolveScheduleAsPerFromTaggedFields(out);
  Object.keys(out).forEach((tag) => {
    if (isScheduleAsPerTag(tag)) delete out[tag];
  });
  if (hasScheduleAsPerMappedField) {
    out.gdSchedule_scheduleasper = scheduleAsPer;
  }
  return out;
}

function resolveCustomMeasDirId(row: Record<string, string>, index: number, customLabel: string): number {
  const normLabel = normalizeDirectionLabel(customLabel);
  for (const key of Object.keys(row)) {
    if (!key.startsWith(MEAS_DIR_ID_BY_LABEL_PREFIX)) continue;
    const suffix = key.slice(MEAS_DIR_ID_BY_LABEL_PREFIX.length);
    if (!suffix.startsWith("custom_")) continue;
    const suffixNorm = suffix.slice("custom_".length);
    if (suffixNorm === String(index) || (normLabel && normalizeDirectionLabel(suffixNorm) === normLabel)) {
      const id = parseInt(String(row[key] ?? "").trim(), 10);
      if (Number.isFinite(id) && id > 0) return id;
    }
  }
  const byIndex = parseInt(
    String(row[`${MEAS_DIR_ID_BY_LABEL_PREFIX}custom_${index}`] ?? "").trim(),
    10
  );
  if (Number.isFinite(byIndex) && byIndex > 0) return byIndex;
  if (normLabel) {
    const byLabel = parseInt(
      String(row[`${MEAS_DIR_ID_BY_LABEL_PREFIX}custom_${normLabel}`] ?? "").trim(),
      10
    );
    if (Number.isFinite(byLabel) && byLabel > 0) return byLabel;
  }
  const generic = parseInt(String(row[`${MEAS_DIR_ID_BY_LABEL_PREFIX}custom`] ?? "").trim(), 10);
  return Number.isFinite(generic) && generic > 0 ? generic : 0;
}

function isDedicatedCustomDirectionField(field: ChecklistFieldDef): boolean {
  if (field.disabled) return false;
  const label = String(field.label ?? "").trim();
  if (!label || normalizeDirectionLabel(label) === "custom") return false;
  return isMeasurementDirectionLabel(label) && !isStandardMeasurementDirectionKey(label);
}

function collectMeasurementDirectionUpdates(
  fields: ChecklistFieldDef[],
  row: Record<string, string>
): SurveyMeasurementDirectionUpdate[] {
  const out: SurveyMeasurementDirectionUpdate[] = [];
  const seen = new Set<string>();

  const pushEntry = (entry: SurveyMeasurementDirectionUpdate) => {
    const key = `${entry.labelKey}::${entry.measureCustom ?? ""}::${entry.measDirId ?? 0}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(entry);
  };

  const dedicatedCustomFields = fields.filter(isDedicatedCustomDirectionField);
  const customField = fields.find(
    (f) => !f.disabled && normalizeDirectionLabel(f.label) === "custom"
  );
  if (customField && dedicatedCustomFields.length === 0) {
    const { labels, values } = parseCustomMeasurementLists(row);
    const count = Math.max(labels.length, values.length);
    for (let i = 0; i < count; i++) {
      const customLabel = String(labels[i] ?? "").trim() || "Custom";
      let rawValue = String(values[i] ?? "").trim();
      if (i === 0) {
        const fromField = String(row[customField.id] ?? "").trim();
        if (fromField) rawValue = fromField;
      }
      const value =
        !rawValue
          ? checklistValueForMappedSave(customField, row[customField.id])
          : checklistValueForMappedSave(customField, rawValue);
      if (!String(value).trim()) continue;
      const measDirId = resolveCustomMeasDirId(row, i, customLabel);
      pushEntry({
        labelKey: "custom",
        value,
        measureCustom: customLabel,
        ...(measDirId > 0 ? { measDirId } : {}),
      });
    }
  }

  fields.forEach((f) => {
    if (f.disabled) return;
    if (normalizeDirectionLabel(f.label) === "custom") return;
    const rawValue = String(row[f.id] ?? "").trim();
    if (!rawValue) return;
    const entry = buildMeasurementDirectionSaveEntry(
      f,
      row,
      checklistValueForMappedSave(f, rawValue)
    );
    if (!entry) return;
    pushEntry(entry);
  });
  return out;
}

function collectFlatTaggedFields(
  fields: ChecklistFieldDef[],
  values: Record<string, string>,
  valueKey: (field: ChecklistFieldDef) => string
): Record<string, string> {
  const out: Record<string, string> = {};
  fields.forEach((f) => {
    if (f.disabled) return;
    const tag = fieldTag(f);
    if (!tag || !isSurveyFlatWriteTag(tag)) return;
    out[tag] = checklistValueForMappedSave(f, values[valueKey(f)]);
  });
  return out;
}

function mergeFlatFields(
  target: Record<string, string>,
  fields: Record<string, string>
): void {
  Object.keys(fields).forEach((tag) => {
    target[tag] = fields[tag];
  });
}

function hierarchyRowMatchesDocument(
  row: Record<string, string>,
  resolvedFd: string,
  rawActiveFd: string
): boolean {
  const rowFd = String(row[SURVEY_HIERARCHY_META.FD_ID] ?? "").trim();
  const targets = new Set([resolvedFd, rawActiveFd].filter(Boolean));
  if (!targets.size) return true;
  if (rowFd && targets.has(rowFd)) return true;
  if (!rowFd && targets.size === 1) return true;
  return false;
}

function buildGeneralDetailsFlatRows(
  generalDetailsByDocument: Record<string, GeneralDetailsDocumentHierarchy> | null | undefined,
  resolvedFd: string
): ReturnType<typeof flattenSurveyHierarchyRows> {
  const empty = {
    schedule: [] as Record<string, string>[],
    survey: [] as Record<string, string>[],
    boundary: [] as Record<string, string>[],
    measurement: [] as Record<string, string>[],
  };
  if (!generalDetailsByDocument || typeof generalDetailsByDocument !== "object") {
    return empty;
  }
  const keys = resolvedFd
    ? Object.keys(generalDetailsByDocument).filter((k) => k === resolvedFd)
    : Object.keys(generalDetailsByDocument);
  keys.forEach((fdKey) => {
    const flat = flattenSurveyHierarchyRows(generalDetailsByDocument[fdKey], fdKey);
    empty.schedule.push(...flat.schedule);
    empty.survey.push(...flat.survey);
    empty.boundary.push(...flat.boundary);
    empty.measurement.push(...flat.measurement);
  });
  return empty;
}

function filterFlatRowsForNature(
  flatRows: ReturnType<typeof flattenSurveyHierarchyRows>,
  naturePropertyKey?: string | null
): ReturnType<typeof flattenSurveyHierarchyRows> {
  const nature = String(naturePropertyKey ?? "").trim();
  if (!nature) return flatRows;
  const filterLevel = (rows: Record<string, string>[]) =>
    rows.filter((r) => String(r[SURVEY_HIERARCHY_META.NATURE_KEY] ?? "") === nature);
  return {
    schedule: filterLevel(flatRows.schedule),
    survey: filterLevel(flatRows.survey),
    boundary: filterLevel(flatRows.boundary),
    measurement: filterLevel(flatRows.measurement),
  };
}

function pickPrimarySurveySourceRow(
  flatRows: ReturnType<typeof flattenSurveyHierarchyRows>,
  fdId: string,
  scheduleId: string,
  naturePropertyKey?: string | null
): Record<string, string> | undefined {
  let rows = flatRows.survey.filter(
    (row) =>
      String(row[SURVEY_HIERARCHY_META.FD_ID] ?? "").trim() === String(fdId) &&
      String(row[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim() === String(scheduleId)
  );
  const nature = String(naturePropertyKey ?? "").trim();
  if (nature) {
    const natureRows = rows.filter(
      (row) => String(row[SURVEY_HIERARCHY_META.NATURE_KEY] ?? "").trim() === nature
    );
    if (natureRows.length) rows = natureRows;
  }
  if (!rows.length) return undefined;
  if (rows.length === 1) return rows[0];
  const sorted = [...rows].sort((a, b) => {
    const aId = parseInt(String(a[SURVEY_HIERARCHY_META.SUR_ID] ?? "").trim(), 10) || 0;
    const bId = parseInt(String(b[SURVEY_HIERARCHY_META.SUR_ID] ?? "").trim(), 10) || 0;
    return bId - aId;
  });
  return sorted[0];
}

function hierarchyRowMatchesGdScheduleNature(
  patchedRow: Record<string, string>,
  groupNatureKey: string | null | undefined,
  flatRows: ReturnType<typeof flattenSurveyHierarchyRows>
): boolean {
  const scheduleId = String(patchedRow[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim();
  if (!scheduleId) return true;
  const gdSchedule = flatRows.schedule.find(
    (r) => String(r[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim() === scheduleId
  );
  if (!gdSchedule) return true;
  const gdNature = String(gdSchedule[SURVEY_HIERARCHY_META.NATURE_KEY] ?? "").trim();
  const rowNature = String(
    patchedRow[SURVEY_HIERARCHY_META.NATURE_KEY] ?? groupNatureKey ?? ""
  ).trim();
  if (gdNature && rowNature && rowNature !== gdNature) return false;
  return true;
}

function hierarchyLevelForWriteTag(tag: string): SurveyHierarchyLevel | null {
  const t = tag.trim();
  if (!t) return null;
  if (t.startsWith("gdSchedule_")) return "schedule";
  if (t.startsWith("gdSurvey_")) return "survey";
  if (t.startsWith("gdBoundary_")) return "boundary";
  if (t.startsWith("gdMeasure_") || t.startsWith("gdMeasDir_")) return "measurement";
  return null;
}

function splitFieldsByHierarchyWriteLevel(
  fields: Record<string, string>
): Partial<Record<SurveyHierarchyLevel, Record<string, string>>> {
  const out: Partial<Record<SurveyHierarchyLevel, Record<string, string>>> = {};
  Object.entries(fields).forEach(([tag, val]) => {
    const level = hierarchyLevelForWriteTag(tag);
    if (!level) return;
    if (!out[level]) out[level] = {};
    out[level]![tag] = val;
  });
  return out;
}

function resolveHierarchyRowIdForLevel(
  level: SurveyHierarchyLevel,
  patchedRow: Record<string, string>,
  section: ChecklistSectionDef,
  repeatableValues: Record<string, Record<string, string>[]>,
  resolvedFd: string,
  naturePropertyKey?: string | null
): number {
  const metaKey = HIERARCHY_LEVEL_META_ID[level];
  const fromRow = parseInt(String(patchedRow[metaKey] ?? "").trim(), 10);
  if (Number.isFinite(fromRow) && fromRow > 0) return fromRow;

  if (level !== "survey") return 0;

  const scheduleId = String(patchedRow[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim();
  if (!scheduleId) return 0;
  const surveyGroup = surveyHierarchyGroupForLevel(section, "survey", naturePropertyKey);
  if (!surveyGroup) return 0;
  const rk = repeatableEntriesKey(section.id, surveyGroup.id);
  const match = (repeatableValues[rk] || []).find((row) => {
    if (resolvedFd && String(row[SURVEY_HIERARCHY_META.FD_ID] ?? "").trim() !== resolvedFd) {
      return false;
    }
    if (String(row[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim() !== scheduleId) {
      return false;
    }
    if (naturePropertyKey) {
      const rowNature = String(row[SURVEY_HIERARCHY_META.NATURE_KEY] ?? "").trim();
      if (rowNature && rowNature !== naturePropertyKey) return false;
    }
    return true;
  });
  if (!match) return 0;
  const surId = parseInt(String(match[SURVEY_HIERARCHY_META.SUR_ID] ?? "").trim(), 10);
  return Number.isFinite(surId) && surId > 0 ? surId : 0;
}

function pushHierarchyRowUpdate(
  hierarchyRows: SurveyHierarchyRowUpdate[],
  hierarchyRowIndex: Map<string, number>,
  level: SurveyHierarchyLevel,
  rowId: number,
  patchedRow: Record<string, string>,
  fields: Record<string, string>,
  measurementDirections: SurveyMeasurementDirectionUpdate[] = []
): void {
  const rowIdKey = HIERARCHY_LEVEL_META_ID[level];
  const resolvedRowId =
    Number.isFinite(rowId) && rowId > 0
      ? rowId
      : parseInt(String(patchedRow[rowIdKey] ?? "").trim(), 10);
  if (!Number.isFinite(resolvedRowId) || resolvedRowId <= 0) return;

  const cleanFields = Object.keys(fields).reduce<Record<string, string>>((acc, tag) => {
    const val = String(fields[tag] ?? "");
    if (val.trim() === "") return acc;
    acc[tag] = val;
    return acc;
  }, {});

  const key = `${level}::${resolvedRowId}`;
  const existingIndex = hierarchyRowIndex.get(key);
  if (existingIndex != null) {
    if (Object.keys(cleanFields).length) {
      hierarchyRows[existingIndex].fields = {
        ...hierarchyRows[existingIndex].fields,
        ...cleanFields,
      };
    }
    if (measurementDirections.length) {
      hierarchyRows[existingIndex].measurementDirections = measurementDirections;
    }
    return;
  }

  if (!Object.keys(cleanFields).length && !measurementDirections.length) return;
  hierarchyRowIndex.set(key, hierarchyRows.length);
  hierarchyRows.push({
    level,
    rowId: resolvedRowId,
    meta: buildHierarchyRowMetaIds(patchedRow),
    fields: cleanFields,
    ...(measurementDirections.length ? { measurementDirections } : {}),
  });
}

/** Merge duplicate hierarchy rows and drop survey tags from schedule-level fallbacks. */
function coalesceHierarchyRows(rows: SurveyHierarchyRowUpdate[]): SurveyHierarchyRowUpdate[] {
  const byKey = new Map<string, SurveyHierarchyRowUpdate>();
  rows.forEach((row) => {
    const key = `${row.level}::${row.rowId}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        ...row,
        fields: { ...(row.fields || {}) },
        meta: row.meta ? { ...row.meta } : undefined,
        measurementDirections: row.measurementDirections
          ? [...row.measurementDirections]
          : undefined,
      });
      return;
    }
    existing.fields = { ...(existing.fields || {}), ...(row.fields || {}) };
    if (row.meta) {
      existing.meta = { ...(existing.meta || {}), ...row.meta };
    }
    if (row.measurementDirections?.length) {
      existing.measurementDirections = row.measurementDirections;
    }
  });

  const merged = Array.from(byKey.values());
  const schedulesWithSurveyRows = new Set<number>();
  merged.forEach((row) => {
    if (row.level !== "survey") return;
    const scheduleId = row.meta?.scheduleId;
    if (scheduleId && scheduleId > 0) schedulesWithSurveyRows.add(scheduleId);
  });
  merged.forEach((row) => {
    if (row.level !== "schedule") return;
    const scheduleId = row.meta?.scheduleId ?? row.rowId;
    if (!schedulesWithSurveyRows.has(scheduleId)) return;
    Object.keys(row.fields || {}).forEach((tag) => {
      if (tag.startsWith("gdSurvey_")) delete row.fields![tag];
    });
  });
  return merged.filter(
    (row) =>
      Object.keys(row.fields || {}).length > 0 ||
      (row.measurementDirections && row.measurementDirections.length > 0)
  );
}

function applyNatureScopedOverridesToHierarchyFields(
  section: ChecklistSectionDef,
  fields: Record<string, string>,
  checklistValues: Record<string, string>,
  patchedRow: Record<string, string>,
  naturePropertyKey?: string | null
): Record<string, string> {
  const fdId = String(patchedRow[SURVEY_HIERARCHY_META.FD_ID] ?? "").trim();
  const scheduleId = String(patchedRow[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim();
  if (!fdId || !scheduleId) return fields;
  const natureKey = String(naturePropertyKey ?? "").trim();
  const natureGroups = natureKey
    ? resolveVisibleSurveyNatureGroups(section, new Set([natureKey]))
    : resolveVisibleSurveyNatureGroups(section, new Set());
  const out = { ...fields };
  natureGroups.forEach((group) => {
    group.fields.forEach((field) => {
      const tag = fieldTag(field);
      if (!tag || !isSurveyHierarchyWriteTag(tag)) return;
      // Nature-scoped keys are per schedule (sv:fd:schedule:field), not per survey.
      // Prefill fills them from the primary/last survey row. Applying those values
      // onto every survey/boundary/measurement hierarchy row clones the last survey
      // into all GD survey rows. Only schedule-level tags may override here.
      const writeLevel = hierarchyLevelForWriteTag(tag);
      if (writeLevel && writeLevel !== "schedule") return;
      const scopedKey = surveyNatureFieldScopeKey(fdId, scheduleId, field.id);
      const scopedVal = String(checklistValues[scopedKey] ?? "").trim();
      if (!scopedVal) return;
      out[tag] = checklistValueForMappedSave(field, scopedVal);
      if (tag === "gdBoundary_measurement") {
        out.gdBoundary_land_aries = out[tag];
      } else if (tag === "gdBoundary_land_aries") {
        out.gdBoundary_measurement = out[tag];
      }
    });
  });
  return out;
}

/** Build survey hierarchy + flat verification write-back payloads. */
export function buildSurveyVerificationUpdates(
  sections: ChecklistSectionDef[],
  checklistValues: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  options?: {
    activeDocumentFdId?: string | number | null;
    naturePropertyKeys?: Set<string>;
    scrutinyApiData?: unknown;
    generalDetailsByDocument?: Record<string, GeneralDetailsDocumentHierarchy> | null;
  }
): {
  hierarchyRows: SurveyHierarchyRowUpdate[];
  flatByFdId: SurveyFlatVerificationUpdate[];
  natureScoped: SurveyNatureScopedUpdate[];
} {
  const hierarchyRows: SurveyHierarchyRowUpdate[] = [];
  const hierarchyRowIndex = new Map<string, number>();
  const flatByFdMap = new Map<number, Record<string, string>>();
  const natureScopedMap = new Map<string, SurveyNatureScopedUpdate>();
  const natureKeys = options?.naturePropertyKeys ?? new Set<string>();
  const rawActiveFd = String(options?.activeDocumentFdId ?? "").trim();
  const gdByDoc = {
    ...(options?.scrutinyApiData as { generalDetailsByDocument?: Record<string, GeneralDetailsDocumentHierarchy> })
      ?.generalDetailsByDocument,
    ...(options?.generalDetailsByDocument ?? {}),
  };
  const fdIdsWithSchedules = listSurveyDocumentFdIdsWithSchedules(gdByDoc);
  const resolvedFd =
    resolveSurveyDocumentFdWithSchedules(options?.activeDocumentFdId, {
      ...(options?.scrutinyApiData as object),
      generalDetailsByDocument: gdByDoc,
    }) || rawActiveFd;
  // Survey verification write-back is file-level: Existing building + Vacant land
  // (and other natures) may live on different deeds. Never scope GD flat rows /
  // hierarchy matching to a single selected document when multiple schedule docs exist.
  const multiDocSchedules = fdIdsWithSchedules.length > 1;
  const flatRows = buildGeneralDetailsFlatRows(
    gdByDoc,
    multiDocSchedules ? "" : resolvedFd
  );
  const docMatchResolvedFd = multiDocSchedules ? "" : resolvedFd;
  const docMatchRawActiveFd = multiDocSchedules ? "" : rawActiveFd;

  const ensureFlat = (fdId: number): Record<string, string> => {
    if (!flatByFdMap.has(fdId)) flatByFdMap.set(fdId, {});
    return flatByFdMap.get(fdId)!;
  };

  const upsertHierarchyScopedFields = (
    level: SurveyHierarchyLevel,
    sourceRow: Record<string, string>,
    fields: Record<string, string>
  ): void => {
    const rowIdKey = HIERARCHY_LEVEL_META_ID[level];
    const rowId = parseInt(String(sourceRow[rowIdKey] ?? "").trim(), 10);
    if (!Number.isFinite(rowId) || rowId <= 0) return;
    const cleanFields = Object.keys(fields).reduce<Record<string, string>>((acc, tag) => {
      const val = String(fields[tag] ?? "");
      if (val.trim() === "") return acc;
      acc[tag] = val;
      return acc;
    }, {});
    if (!Object.keys(cleanFields).length) return;
    const key = `${level}::${rowId}`;
    const existingIndex = hierarchyRowIndex.get(key);
    if (existingIndex != null) {
      hierarchyRows[existingIndex].fields = {
        ...hierarchyRows[existingIndex].fields,
        ...cleanFields,
      };
      return;
    }
    hierarchyRowIndex.set(key, hierarchyRows.length);
    hierarchyRows.push({
      level,
      rowId,
      meta: buildHierarchyRowMetaIds(sourceRow),
      fields: cleanFields,
    });
  };

  sections.forEach((section) => {
    if (sectionFlowMode(section) !== "survey_verification") return;

    surveyHierarchyLinkedGroups(section).forEach((group) => {
      const level = group.surveyHierarchyLevel;
      if (!level) return;
      const key = repeatableEntriesKey(section.id, group.id);
      const groupFlatRows = filterFlatRowsForNature(flatRows, group.naturePropertyKey);
      (repeatableValues[key] || []).forEach((row) => {
        const patchedRow = enrichHierarchyRowMeta(
          level,
          patchHierarchyRowMeta(level, row, groupFlatRows),
          groupFlatRows
        );
        if (!hierarchyRowMatchesDocument(patchedRow, docMatchResolvedFd, docMatchRawActiveFd)) {
          return;
        }
        if (!hierarchyRowMatchesGdScheduleNature(patchedRow, group.naturePropertyKey, groupFlatRows)) {
          return;
        }
        const fields = applyNatureScopedOverridesToHierarchyFields(
          section,
          collectHierarchyTaggedFields(group.fields, patchedRow),
          checklistValues,
          patchedRow,
          group.naturePropertyKey
        );
        const measurementDirections =
          level === "measurement" ? collectMeasurementDirectionUpdates(group.fields, patchedRow) : [];
        const fieldsByLevel = splitFieldsByHierarchyWriteLevel(fields);
        const rowFd =
          String(patchedRow[SURVEY_HIERARCHY_META.FD_ID] ?? "").trim() || resolvedFd;

        if (level === "measurement" && measurementDirections.length) {
          const rowId = resolveHierarchyRowIdForLevel(
            "measurement",
            patchedRow,
            section,
            repeatableValues,
            rowFd,
            group.naturePropertyKey
          );
          if (rowId > 0) {
            pushHierarchyRowUpdate(
              hierarchyRows,
              hierarchyRowIndex,
              "measurement",
              rowId,
              patchedRow,
              fieldsByLevel.measurement || {},
              measurementDirections
            );
          }
          return;
        }

        const targetLevels = new Set<SurveyHierarchyLevel>([
          level,
          ...(Object.keys(fieldsByLevel) as SurveyHierarchyLevel[]),
        ]);
        targetLevels.forEach((targetLevel) => {
          const levelFields = fieldsByLevel[targetLevel];
          if (!levelFields || !Object.keys(levelFields).length) return;
          const rowId = resolveHierarchyRowIdForLevel(
            targetLevel,
            patchedRow,
            section,
            repeatableValues,
            rowFd,
            group.naturePropertyKey
          );
          if (rowId <= 0) return;
          pushHierarchyRowUpdate(
            hierarchyRows,
            hierarchyRowIndex,
            targetLevel,
            rowId,
            patchedRow,
            levelFields
          );
        });
      });
    });

    const visibleNatureGroups = resolveVisibleSurveyNatureGroups(section, natureKeys);
    visibleNatureGroups.forEach((g) => {
      const hasHierarchy = surveyHierarchyLinkedGroups(section).some((hg) => hg.fields.length > 0);
      if (hasHierarchy && (resolvedFd || fdIdsWithSchedules.length > 0)) {
        g.fields.forEach((field) => {
          if (field.disabled) return;
          const tag = fieldTag(field);
          if (
            !tag ||
            (!isSurveyFlatWriteTag(tag) &&
              !isSurveyNatureScopedScheduleTag(tag) &&
              !isSurveyHierarchyWriteTag(tag))
          ) {
            return;
          }
          Object.keys(checklistValues).forEach((scopedKey) => {
            const parsed = parseSurveyNatureFieldScopeKey(scopedKey);
            if (!parsed || parsed.fieldId !== field.id) return;
            const schedId = parseInt(parsed.scheduleId, 10);
            const fdId = parseInt(parsed.fdId, 10);
            if (!Number.isFinite(schedId) || schedId <= 0 || !Number.isFinite(fdId) || fdId <= 0) {
              return;
            }
            const savedVal = checklistValueForMappedSave(field, checklistValues[scopedKey]);
            if (!String(savedVal ?? "").trim()) return;
            if (isSurveyHierarchyWriteTag(tag)) {
              const level = hierarchyLevelForWriteTag(tag);
              if (!level) return;
              // Hierarchy repeatable groups already write each survey/boundary/
              // measurement row with its own sur_id. Nature-scoped keys are
              // schedule-scoped and would stamp the primary/last survey onto one
              // row (or every row via overrides) — skip child levels here.
              if (level !== "schedule") return;
              const scopedFields: Record<string, string> = { [tag]: savedVal };
              // Keep boundary extent aliases in sync for mixed master mappings.
              if (tag === "gdBoundary_measurement") {
                scopedFields.gdBoundary_land_aries = savedVal;
              } else if (tag === "gdBoundary_land_aries") {
                scopedFields.gdBoundary_measurement = savedVal;
              }
              const sourceRow =
                pickPrimarySurveySourceRow(flatRows, String(fdId), String(schedId), g.naturePropertyKey) ??
                pickPrimarySurveySourceRow(flatRows, String(fdId), String(schedId));
              if (sourceRow) {
                upsertHierarchyScopedFields(level, sourceRow, scopedFields);
                return;
              }
              // Fallback when GD flat rows are missing: schedule-linked update.
              if (level === "schedule" && tag.startsWith("gdSchedule_")) {
                const key = `schedule::${schedId}`;
                const existingIndex = hierarchyRowIndex.get(key);
                if (existingIndex != null) {
                  hierarchyRows[existingIndex].fields = {
                    ...hierarchyRows[existingIndex].fields,
                    ...scopedFields,
                  };
                } else {
                  const scheduleRowId = parseInt(String(schedId).trim(), 10);
                  if (Number.isFinite(scheduleRowId) && scheduleRowId > 0) {
                    hierarchyRowIndex.set(key, hierarchyRows.length);
                    hierarchyRows.push({
                      level: "schedule",
                      rowId: scheduleRowId,
                      meta: {
                        scheduleId: scheduleRowId,
                      },
                      fields: scopedFields,
                    });
                  }
                }
              }
              return;
            }
            const mapKey = `${fdId}::${schedId}`;
            if (!natureScopedMap.has(mapKey)) {
              natureScopedMap.set(mapKey, { fdId, scheduleId: schedId, fields: {} });
            }
            natureScopedMap.get(mapKey)!.fields[tag] = savedVal;
          });
        });
        return;
      }

      const flatFields = collectFlatTaggedFields(g.fields, checklistValues, (f) => f.id);
      if (Object.keys(flatFields).length && resolvedFd) {
        const fdId = parseInt(resolvedFd, 10);
        if (Number.isFinite(fdId) && fdId > 0) {
          mergeFlatFields(ensureFlat(fdId), flatFields);
        }
      }
    });

    const ecGroupFieldIds = new Set(
      surveyLinkedGroups(section).flatMap((g) => g.fields.map((f) => f.id))
    );
    section.fields
      .filter((f) => !ecGroupFieldIds.has(f.id))
      .forEach((f) => {
        const tag = fieldTag(f);
        if (!tag || !isSurveyFlatWriteTag(tag) || f.disabled) return;
        if (!resolvedFd) return;
        const fdId = parseInt(resolvedFd, 10);
        if (!Number.isFinite(fdId) || fdId <= 0) return;
        ensureFlat(fdId)[tag] = checklistValueForMappedSave(f, checklistValues[f.id]);
      });
  });

  return {
    hierarchyRows: coalesceHierarchyRows(hierarchyRows),
    flatByFdId: Array.from(flatByFdMap.entries()).map(([fdId, fields]) => ({ fdId, fields })),
    natureScoped: Array.from(natureScopedMap.values()),
  };
}
